import { useQuery } from '@tanstack/react-query'
import instance from '../api'
import { Subscription } from '../../interfaces/subscription'
import { useAuthContext } from '../../context/auth/auth'

// Most-relevant first. A bank transfer subscription sits at `pending` until a
// super administrator confirms the transfer arrived, so filtering the request
// down to status=active would hide the subscription from exactly the customers
// who still need to act on it - including the Confirm Payment button, which
// needs the subscription id.
const STATUS_PRIORITY: Subscription['status'][] = [
  'active',
  'trialing',
  'pending',
  'past_due',
  'unpaid',
]

/** The one key this subscription is cached under. Exported so the mutations
 *  that change a subscription invalidate the query that reads it - they used
 *  to name `['activeSubscription']`, the key this hook carried before it was
 *  renamed, which matches nothing and so refreshed nothing. */
export const CURRENT_SUBSCRIPTION_QUERY_KEY = ['currentSubscription']

// react-query v4 rejects a query that resolves to `undefined`, so "no
// subscription" has to be an explicit null.
const pickCurrent = (subscriptions: Subscription[]): Subscription | null => {
  for (const status of STATUS_PRIORITY) {
    const match = subscriptions.find(
      (subscription) => subscription.status === status,
    )
    if (match) return match
  }
  return null
}

const getCurrentSubscription = async (
  token: string,
): Promise<Subscription | null> => {
  // The backend already scopes this list to the requesting user.
  const response = await instance.get('/payment/subscriptions/', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  const subscriptions: Subscription[] = response.data.data ?? []
  return pickCurrent(subscriptions)
}

const useCurrentSubscription = () => {
  const { token } = useAuthContext()
  return useQuery({
    queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
    queryFn: () => getCurrentSubscription(token!),
    // Public pages (the reporter form, checking a report) mount components
    // that ask for limited mode; without a token there is nothing to fetch.
    enabled: Boolean(token),
    staleTime: 20000,
  })
}

export default useCurrentSubscription
